import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, sessionStore, tokenStore, SESSION_EXPIRED_EVENT } from "@/lib/api";

const AuthContext = createContext(null);

// Affichée quand la session est coupée par la durée de vie absolue (24 h)
// et non par une action explicite de l'utilisateur.
const SESSION_EXPIRED_MESSAGE =
  "Session expirée après 24 h : reconnectez-vous pour continuer.";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Plus d'exception liée à un fragment `#session_id=…` : la connexion par
    // annuaire (lot 3) renvoie le navigateur avec les cookies déjà posés, donc
    // `/auth/me` est la seule source de vérité — et une URL forgée ne peut plus
    // faire croire à une session en cours.
    let cancelled = false;
    const checkSession = async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) {
          setUser(data);
          // Session rétablie : on (re)pose l'échéance absolue de 24 h à
          // partir de l'horodatage de login porté par le token (une échéance
          // déjà mémorisée n'est jamais repoussée par cet alignement).
          sessionStore.sync(tokenStore.get());
        }
      } catch (error) {
        if (error?.response?.status && error.response.status !== 401) {
          console.error("Auth /me error:", error);
        }
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    checkSession();
    return () => { cancelled = true; };
  }, []);

  /**
   * Coupe la session côté client : échéance, token, puis état utilisateur —
   * les routes protégées basculent aussitôt sur /login.
   * `callServer=false` évite d'appeler /auth/logout quand le serveur a déjà
   * refusé de renouveler la session (inutile de lui redemander).
   */
  const endSession = useCallback(async ({ notify = false, callServer = true } = {}) => {
    sessionStore.clear();
    if (callServer) {
      try {
        await api.post("/auth/logout");
      } catch (error) {
        console.error("Logout error:", error);
      }
    }
    tokenStore.clear();
    setUser(null);
    // `id` fixe : si plusieurs requêtes échouent en même temps, un seul toast.
    if (notify) toast.info(SESSION_EXPIRED_MESSAGE, { id: SESSION_EXPIRED_EVENT });
  }, []);

  const login = useCallback(async (email, password, code) => {
    const payload = { email, password };
    if (code) payload.code = code;
    const { data } = await api.post("/auth/login", payload);
    if (data.access_token) tokenStore.set(data.access_token);
    // Nouvelle session → nouvelle échéance absolue (24 h à partir de maintenant).
    sessionStore.start();
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) tokenStore.set(data.access_token);
    sessionStore.start();
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => endSession(), [endSession]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (error) {
      console.error("Refresh /me error:", error);
      return null;
    }
  }, []);

  // Déconnexion instantanée à l'échéance des 24 h : le timer est armé dès
  // qu'un utilisateur est connecté, donc même un onglet resté ouvert sans
  // activité est déconnecté à l'heure exacte, suivi de la fenêtre de login.
  useEffect(() => {
    if (!user) return undefined;
    let deadline = sessionStore.deadline();
    if (deadline == null) deadline = sessionStore.sync(tokenStore.get());
    if (!Number.isFinite(deadline)) return undefined;
    const timer = window.setTimeout(
      () => { endSession({ notify: true }); },
      Math.max(deadline - Date.now(), 0)
    );
    return () => window.clearTimeout(timer);
  }, [user, endSession]);

  // Réaction à un refus de renouvellement côté serveur (intercepteur axios) :
  // la session est morte → on coupe tout de suite, sans nouvel appel API.
  useEffect(() => {
    const onSessionExpired = () => { endSession({ notify: true, callServer: false }); };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [endSession]);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
